import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canManageUnion } from '../../../shared/domain/unionAccess.js';
import { loadRaidStrategyPlans, saveRaidStrategyPhase, saveRaidStrategyPlan } from '../api/raidStrategyApi.js';

function planKey(bossNo, phaseNo) {
  return `${Number(bossNo || 0)}:${Number(phaseNo || 0)}`;
}

export function useRaidStrategyPlans(seasonNo, { viewerRole = '' } = {}) {
  const [context, setContext] = useState({
    unionId: '', unionName: '', viewerRole: '', canManage: false, migrationRequired: false, seasonId: '', seasonNo: 0, plans: [],
  });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const runRef = useRef(0);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    const runId = runRef.current + 1;
    runRef.current = runId;
    if (!silent) setLoading(true);
    try {
      const next = await loadRaidStrategyPlans(seasonNo);
      if (runRef.current !== runId) return null;
      setContext(next);
      setErrorMessage('');
      return next;
    } catch (error) {
      if (runRef.current !== runId) return null;
      if (error?.code === 'strategy_migration_required') {
        const preview = {
          unionId: '', unionName: '', viewerRole, canManage: canManageUnion(viewerRole), migrationRequired: true,
          seasonId: '', seasonNo: Number(seasonNo || 0), plans: [],
        };
        setContext(preview);
        setErrorMessage('');
        return preview;
      }
      setErrorMessage(error instanceof Error ? error.message : '전략판 계획을 불러오지 못했습니다.');
      return null;
    } finally {
      if (runRef.current === runId && !silent) setLoading(false);
    }
  }, [seasonNo, viewerRole]);

  useEffect(() => {
    refresh();
    const changed = () => refresh({ silent: true });
    window.addEventListener('dororaid:raid-strategy-changed', changed);
    window.addEventListener('dororaid:raid-season-changed', changed);
    return () => {
      runRef.current += 1;
      window.removeEventListener('dororaid:raid-strategy-changed', changed);
      window.removeEventListener('dororaid:raid-season-changed', changed);
    };
  }, [refresh]);

  const plansByKey = useMemo(() => new Map(
    context.plans.map((plan) => [planKey(plan.bossNo, plan.phaseNo), plan]),
  ), [context.plans]);

  const replaceSavedPlans = useCallback((savedPlans = []) => {
    const savedKeys = new Set(savedPlans.map((plan) => planKey(plan.bossNo, plan.phaseNo)));
    setContext((current) => ({
      ...current,
      plans: [
        ...current.plans.filter((item) => !savedKeys.has(planKey(item.bossNo, item.phaseNo))),
        ...savedPlans,
      ].sort((left, right) => left.bossNo - right.bossNo || left.phaseNo - right.phaseNo),
    }));
  }, []);

  const save = useCallback(async (plan) => {
    if (context.migrationRequired) {
      const error = new Error('전략판 DB 적용 후 공용 저장을 사용할 수 있습니다.');
      error.code = 'strategy_migration_required';
      throw error;
    }
    const key = planKey(plan?.bossNo, plan?.phaseNo);
    setSavingKey(key);
    try {
      const saved = await saveRaidStrategyPlan({ ...plan, seasonNo });
      replaceSavedPlans([saved]);
      setErrorMessage('');
      window.dispatchEvent(new CustomEvent('dororaid:raid-strategy-changed', {
        detail: { seasonNo: Number(seasonNo), bossNo: saved.bossNo, phaseNo: saved.phaseNo },
      }));
      return saved;
    } catch (error) {
      const message = error instanceof Error ? error.message : '전략판 계획을 저장하지 못했습니다.';
      setErrorMessage(message);
      throw error;
    } finally {
      setSavingKey('');
    }
  }, [context.migrationRequired, replaceSavedPlans, seasonNo]);

  const savePhase = useCallback(async (phaseNo, plans) => {
    if (context.migrationRequired) {
      const error = new Error('전략판 DB 적용 후 공용 저장을 사용할 수 있습니다.');
      error.code = 'strategy_migration_required';
      throw error;
    }
    const key = `phase:${Number(phaseNo)}`;
    setSavingKey(key);
    try {
      const saved = await saveRaidStrategyPhase({ seasonNo, phaseNo, plans });
      replaceSavedPlans(saved);
      setErrorMessage('');
      window.dispatchEvent(new CustomEvent('dororaid:raid-strategy-changed', {
        detail: { seasonNo: Number(seasonNo), phaseNo: Number(phaseNo) },
      }));
      return saved;
    } catch (error) {
      const message = error instanceof Error ? error.message : '단수별 전략판을 저장하지 못했습니다.';
      setErrorMessage(message);
      throw error;
    } finally {
      setSavingKey('');
    }
  }, [context.migrationRequired, replaceSavedPlans, seasonNo]);

  return {
    ...context,
    plansByKey,
    loading,
    savingKey,
    errorMessage,
    refresh,
    save,
    savePhase,
    planKey,
  };
}
